import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { notificationAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const socket = useSocket();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const audioContextRef = useRef(null);

    // Initialize Audio Context on demand (browsers require user interaction usually, but we try)
    const initAudio = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
    };

    const playNotificationSound = () => {
        try {
            initAudio();
            const ctx = audioContextRef.current;
            if (ctx.state === 'suspended') {
                ctx.resume();
            }
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(500, ctx.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(1000, ctx.currentTime + 0.1);
            
            gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

            oscillator.start();
            oscillator.stop(ctx.currentTime + 0.5);
        } catch (error) {
            console.error('Error playing sound:', error);
        }
    };

    const fetchNotifications = async () => {
        if (!user) return;
        try {
            const [listRes, countRes] = await Promise.all([
                notificationAPI.getAll({ limit: 10 }),
                notificationAPI.getUnreadCount()
            ]);
            setNotifications(listRes.data?.data?.notifications || []);
            setUnreadCount(countRes.data?.data?.count || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationAPI.markAsRead(id);
            setUnreadCount(prev => Math.max(0, prev - 1));
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, isRead: true } : n)
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationAPI.markAllAsRead();
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchNotifications();
        }
    }, [user]);

    useEffect(() => {
        if (!socket) return;

        const handleMessage = (event) => {
            try {
                // If the event is raw message event
                const message = event instanceof MessageEvent ? JSON.parse(event.data) : event;
                
                // Check format. Backend sends: { type: 'notification', type: 'new_appointment', data: {...} }
                // Wait, backend wrapper sends: { type: 'notification', data: { type: 'new_appointment', ... } }
                
                if (message.type === 'notification') {
                    const notificationData = message.data; // This is the inner data object
                    
                    // Optimistically add to list
                    const newNotification = {
                        _id: Date.now().toString(), // Temp ID until refresh
                        ...notificationData,
                        isRead: false,
                        createdAt: new Date().toISOString()
                    };
                    
                    setNotifications(prev => [newNotification, ...prev]);
                    setUnreadCount(prev => prev + 1);
                    
                    playNotificationSound();
                    
                    // Show toast
                    if (notificationData.data?.patientName) {
                         toast.info(t('common.newAppointment', { patientName: notificationData.data.patientName }));
                    } else {
                         toast.info(t('common.newNotification'));
                    }
                }
            } catch (error) {
                console.error('Error handling socket message in NotificationContext:', error);
            }
        };

        // Note: SocketContext implementation might already parse JSON. 
        // Let's check SocketContext usage. 
        // It provides the raw websocket object. The listener in SocketContext logs it but doesn't expose a listener attacher directly?
        // Ah, `useSocket` returns the ws instance. I can attach onmessage.
        // BUT `SocketContext.js` ALREADY has an onmessage handler!
        // Attaching another one to `ws.onmessage` will OVERWRITE the previous one.
        // I should probably modify `SocketContext` to dispatch events or use `addEventListener` if it's a DOM WebSocket (it is).
        
        // Standard WebSocket API supports `addEventListener('message', ...)`
        
        socket.addEventListener('message', handleMessage);

        return () => {
            socket.removeEventListener('message', handleMessage);
        };
    }, [socket, t]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            fetchNotifications,
            markAsRead,
            markAllAsRead
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
